import type { ShellSlice } from '../bs/shell'

/**
 * English for the application frame.
 *
 * The three plural keys all say the same thing, which is correct rather than
 * lazy: English has two forms and the split is 1 / everything else, so the
 * Slavic 1 / 2–4 / 5+ buckets collapse. Writing "{count} unread" three times is
 * how a three-slot rule is answered by a two-form language.
 */
export const shell: ShellSlice = {
  'brand.tagline': 'Control and improvement',
  'brand.illustrationAlt': 'Scanning a QR label on equipment in the plant',

  'language.label': 'Language',
  'language.change': 'Change language',

  'theme.light': 'Light theme',
  'theme.dark': 'Dark theme',
  'theme.system': 'Follow the system',
  'theme.tooltip': '{current} · next: {next}',

  'nav.home': 'Home',
  'nav.home.hint': 'Overview and quick actions',
  'nav.submissions': 'Reports',
  'nav.submissions.hint': 'Every report and where it stands',
  'nav.tasks': 'Tasks',
  'nav.tasks.hint': 'Tasks assigned to you',
  'nav.fiveS': '5S',
  'nav.fiveS.hint': 'Work centre scoring',
  'nav.analytics': 'Analytics',
  'nav.analytics.hint': 'Trends and process analysis',
  'nav.admin': 'Administration',
  'nav.admin.hint': 'People, code lists and the audit log',
  'nav.owner': 'Back to the console',
  'nav.owner.hint': 'Every company and support',
  'nav.adminBadge': 'ADMIN',
  'nav.mainLabel': 'Main navigation',

  'nav.group.work': 'Work',
  'nav.group.overview': 'Overview',
  'admin.sections': 'Administration pages',
  'header.menu': 'Open the menu',
  'header.toggleSidebar': 'Hide or show the menu',
  'crumb.label': 'Where you are',
  'crumb.submission': 'Report',
  'crumb.walk': '5S walk',
  'crumb.edit': 'Edit',
  'theme.label': 'Theme',

  'quick.new': 'New',
  'quick.sheetTitle': 'What would you like to start?',
  'quick.submission.hint': 'A fault, a hazard or a suggestion',
  'quick.walk': 'New 5S walk',
  'quick.walk.hint': 'Score the order at a workplace',
  'quick.task.hint': 'Assign work on an open report',

  'search.open': 'Search…',
  'search.title': 'Search',
  'search.placeholder': 'Reports, machines, pages…',
  'search.placeholder.people': 'Reports, machines, people, pages…',
  'search.group.recent': 'Recently opened',
  'search.group.actions': 'Quick actions',
  'search.group.pages': 'Pages',
  'search.group.submissions': 'Reports',
  'search.group.workCenters': 'Work centres',
  'search.group.people': 'People',
  'search.more': 'Show all ({count})',
  'search.loading': 'Searching…',
  'search.empty': 'Nothing matches “{query}”.',
  'search.emptyHint': 'Try a report number, a machine code or part of a title.',
  'search.short': 'Type one more character to search reports and machines too.',
  'search.unavailable':
    'Searching reports and machines is not working right now. Pages and actions are still here.',
  'search.results.one': '{count} result',
  'search.results.few': '{count} results',
  'search.results.many': '{count} results',
  'search.keys.move': 'move',
  'search.keys.open': 'open',
  'search.keys.close': 'close',
  'search.action.notifications': 'My notifications',
  'search.action.notificationSettings': 'Notification settings',
  'search.keywords.submission': 'report fault problem hazard idea suggestion',
  'search.keywords.walk': 'score evaluation order cleanliness',
  'search.keywords.task': 'task assign work',
  'search.keywords.notifications': 'alerts bell',
  'search.keywords.notificationSettings': 'email mail summary alerts',
  'search.keywords.profile': 'profile password two-factor sessions account',
  'search.keywords.scan': 'qr code sticker camera',
  'search.keywords.settings': 'company currency time zone',
  'search.keywords.badges': 'cards print code',
  'search.keywords.templates': 'template criteria checklist',

  'account.myAccount': 'My account',
  'account.ariaLabel': 'Account — {name}',
  'account.signOut': 'Sign out',

  'header.scanQr': 'Scan a QR code',
  'header.scanQr.hint': 'Scan the code on the machine',
  'header.newSubmission': 'New report',

  'notifications.title': 'Notifications',
  'notifications.empty': 'No notifications',
  'notifications.emptyHint':
    'Tasks assigned to you and changes to your reports arrive here.',
  'notifications.allRead': 'Everything is read',
  'notifications.markAllRead': 'Mark all as read',
  'notifications.today': 'Today',
  'notifications.yesterday': 'Yesterday',
  'notifications.earlier': 'Earlier',
  'notifications.unreadMarker': 'unread',
  'notifications.unread.one': '{count} unread',
  'notifications.unread.few': '{count} unread',
  'notifications.unread.many': '{count} unread',

  'queue.title': 'Queue',
  'queue.savedOnDevice': 'Reports are held on this device.',
  'queue.hint': 'Reports send themselves. You can check here what is left.',
  'queue.offlineNotice':
    'No connection to the server right now. Your reports are saved on this device and will be sent as soon as the connection is back — you can close the app.',
  'queue.empty': 'The queue is empty.',
  'queue.waiting': 'Waiting',
  'queue.rejectedBadge': 'Rejected',
  'queue.sendNow': 'Send now',
  'queue.details': 'Details',
  'queue.remove': 'Remove',
  'queue.noConnection': 'No connection.',
  'queue.attachments': '{count} att.',
  'queue.discardConfirm':
    'Remove this report from the queue? It will not be sent and the details are lost.',
  'queue.pending.one': '{count} report waiting to be sent.',
  'queue.pending.few': '{count} reports waiting to be sent.',
  'queue.pending.many': '{count} reports waiting to be sent.',
  'queue.rejected.one': '{count} report was not accepted.',
  'queue.rejected.few': '{count} reports were not accepted.',
  'queue.rejected.many': '{count} reports were not accepted.',

  'support.badge': 'Support',
  'support.leave': 'Leave the session',
  'support.ended': 'The support session has ended.',
  'support.endFailed': 'The support session could not be ended.',

  'error.technicalDetails': 'Technical details',
  'error.goHome': 'Home',
  'boot.starting': 'Starting the app',
  'boot.loadingLayout': 'Loading the layout',
  'boot.checkingSession': 'Checking the session',
  'boot.aria': 'Loading: {step}',
  'boot.ready': 'Ready',
  'boot.loadingAssets': 'Loading resources',

  'nav.guide': 'Guide',
  'nav.guide.hint': 'How it works, for your role',
  'module.submissions': 'Reports and tasks',
  'module.fiveS': '5S',
  'module.analytics': 'Analytics',
  'module.mes': 'KaproMES integration',
  'module.disabled.title': 'This module is not switched on for your company.',
  'module.disabled.description':
    '“{module}” is not part of your app at the moment. Your data is kept — if you need this part, ask your administrator.',
  'module.disabled.home': 'Go to the home screen',

  // ---- No plant -----------------------------------------------------
  'plants.none.title': 'You have not been given any plant — ask your administrator',
  'plants.none.body':
    'Reports, tasks, 5S and analytics are shown by plant, and you have none yet. As soon as an administrator gives you one, everything from it appears here.',
  'plants.none.retry': 'Check again',
}
